import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { ArrowRight, ShieldCheck, Truck, PackageCheck, HeartHandshake, Search, ShoppingBag, Smile, Plus, CheckCircle2, Lock } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useCurrency } from '@/contexts/CurrencyContext';
import { HealthProductCard } from '@/components/health/HealthProductCard';
import { FloatingShapes } from '@/components/health/FloatingShapes';
import { HEALTH_CATEGORIES, HERO_IMAGE, PHARMACIST_IMAGE, categoryUrl } from '@/lib/health';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.6, delay, ease: EASE },
});

const Overline = ({ children, color = 'text-health-emerald' }) => (
  <span className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] ${color}`}>
    <Plus className="h-3 w-3" strokeWidth={3} /> {children}
  </span>
);

const HeroChip = ({ icon: Icon, text, className, delay }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.6 }}
    animate={{ opacity: 1, scale: 1, y: [0, -10, 0] }}
    transition={{ opacity: { delay }, scale: { delay, type: 'spring' }, y: { duration: 4, repeat: Infinity, ease: 'easeInOut', delay } }}
    className={`absolute flex items-center gap-2 bg-white rounded-2xl px-4 py-3 shadow-lift text-sm font-semibold text-health-ink ${className}`}
  >
    <Icon className="h-5 w-5 text-health-emerald" />
    {text}
  </motion.div>
);

const Hero = () => (
  <section className="relative overflow-hidden pt-28 pb-20 lg:pt-36 lg:pb-28 bg-health-cream" data-testid="hero-section">
    <div className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-health-mint animate-blob" />
    <div className="absolute top-40 -right-32 w-[480px] h-[480px] rounded-full bg-health-peach animate-blob [animation-delay:3s]" />
    <div className="absolute inset-0 dot-pattern opacity-60" />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-center">
      <div className="lg:col-span-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 bg-white rounded-full pl-2 pr-4 py-1.5 shadow-soft mb-8">
          <span className="w-6 h-6 rounded-full bg-health-coral text-white flex items-center justify-center"><Smile className="h-4 w-4" /></span>
          <span className="text-sm font-semibold text-slate-700">Your friendly online pharmacy shelf</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-health-ink leading-[1.02] mb-6"
        >
          Feel better,{' '}
          <span className="relative inline-block text-health-emerald">
            faster
            <svg viewBox="0 0 200 20" className="absolute -bottom-3 left-0 w-full h-4 text-health-coral" aria-hidden="true">
              <motion.path d="M3 14 C 50 2, 150 2, 197 12" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.9, delay: 0.7 }} />
            </svg>
          </span>
          <br />with care at your door.
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }} className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl mb-10">
          Over-the-counter pain &amp; cold relief, first aid essentials and easy-to-use home medical devices, all in one cheerful place. No queues, no fuss.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="flex flex-wrap gap-4 mb-12">
          <Link to="/products">
            <motion.span whileHover={{ y: -3 }} whileTap={{ scale: 0.97 }} className="inline-block">
              <Button className="group h-14 px-8 rounded-full bg-health-emerald hover:bg-health-emerald-dark text-white text-base font-bold shadow-lift" data-testid="hero-shop-button">
                Shop health essentials
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.span>
          </Link>
          <a href="#categories">
            <motion.span whileHover={{ y: -3 }} whileTap={{ scale: 0.97 }} className="inline-block">
              <Button variant="outline" className="h-14 px-8 rounded-full border-2 border-health-ink/10 bg-white text-health-ink hover:bg-health-mint text-base font-bold" data-testid="hero-browse-categories-button">
                Browse categories
              </Button>
            </motion.span>
          </a>
        </motion.div>

        <div className="flex flex-wrap gap-x-8 gap-y-3">
          {['Over-the-counter only', 'Discreet packaging', 'Secure checkout'].map((t, i) => (
            <motion.span key={t} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 + i * 0.1 }} className="flex items-center gap-2 text-sm font-semibold text-slate-600">
              <CheckCircle2 className="h-5 w-5 text-health-emerald" /> {t}
            </motion.span>
          ))}
        </div>
      </div>

      <motion.div initial={{ opacity: 0, scale: 0.9, rotate: -3 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 0.9, delay: 0.2, ease: EASE }} className="lg:col-span-6 relative">
        <div className="relative rounded-[2.5rem] overflow-hidden shadow-lift border-8 border-white">
          <img src={HERO_IMAGE} alt="Friendly pharmacy essentials" className="w-full aspect-[4/3] object-cover" />
        </div>
        <HeroChip icon={Truck} text="Ships fast" className="-top-5 left-4 sm:left-10" delay={0.9} />
        <HeroChip icon={ShieldCheck} text="Genuine products" className="-bottom-6 right-4 sm:right-10" delay={1.1} />
        <HeroChip icon={HeartHandshake} text="Real people, real care" className="hidden sm:flex top-1/2 -left-8" delay={1.3} />
      </motion.div>
    </div>
  </section>
);

const TrustMarquee = () => (
  <section className="py-4 bg-health-emerald text-white">
    <Marquee speed={45} gradient={false} autoFill>
      {['Over-the-counter essentials', 'Discreet delivery', 'Secure checkout', 'Genuine products', 'Friendly support'].map((t) => (
        <span key={t} className="flex items-center mx-8 font-heading text-base font-semibold">
          <Plus className="h-4 w-4 mr-3 text-health-peach" strokeWidth={3} /> {t}
        </span>
      ))}
    </Marquee>
  </section>
);

const CategoryCard = ({ cat, index }) => (
  <motion.div {...fadeUp(index * 0.12)} whileHover={{ y: -10, rotate: index === 1 ? 0 : index === 0 ? -1 : 1 }} transition={{ type: 'spring', stiffness: 260, damping: 20 }}>
    <Link to={categoryUrl(cat.name)} className={`group block ${cat.tint} rounded-[2rem] p-4 h-full`} data-testid={`category-card-${cat.slug}`}>
      <div className="relative rounded-[1.5rem] overflow-hidden aspect-[4/3] bg-white">
        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
        <span className="absolute top-4 left-4 bg-white/95 rounded-full px-3 py-1 text-xs font-bold text-health-ink">{cat.badge}</span>
      </div>
      <div className="px-2 pt-5 pb-2 flex items-end justify-between gap-4">
        <div>
          <h3 className="font-heading text-2xl font-bold text-health-ink mb-1">{cat.name}</h3>
          <p className="text-sm text-slate-600 leading-relaxed">{cat.tagline}</p>
        </div>
        <span className="shrink-0 w-12 h-12 rounded-full bg-white text-health-ink flex items-center justify-center group-hover:bg-health-ink group-hover:text-white transition-colors">
          <ArrowRight className="h-5 w-5 group-hover:-rotate-45 transition-transform duration-300" />
        </span>
      </div>
    </Link>
  </motion.div>
);

const Categories = () => (
  <section id="categories" className="py-24 bg-background scroll-mt-20" data-testid="categories-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <motion.div {...fadeUp()} className="max-w-2xl mb-14">
        <Overline>Shop by need</Overline>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-health-ink mt-4">Everything for the little health moments</h2>
      </motion.div>
      <div className="grid md:grid-cols-3 gap-6">
        {HEALTH_CATEGORIES.map((cat, i) => <CategoryCard key={cat.slug} cat={cat} index={i} />)}
      </div>
    </div>
  </section>
);

const EmptyShelf = () => (
  <motion.div {...fadeUp()} className="relative overflow-hidden rounded-[2rem] bg-health-mint p-10 md:p-14 text-center" data-testid="featured-empty-state">
    <FloatingShapes />
    <motion.div animate={{ rotate: [0, -6, 6, 0] }} transition={{ duration: 3, repeat: Infinity }} className="relative w-20 h-20 mx-auto mb-6 rounded-3xl bg-white shadow-soft flex items-center justify-center">
      <PackageCheck className="h-10 w-10 text-health-emerald" />
    </motion.div>
    <h3 className="relative font-heading text-2xl md:text-3xl font-bold text-health-ink mb-3">Fresh shelves are on the way</h3>
    <p className="relative text-slate-600 max-w-md mx-auto mb-8">We're stocking up on new health essentials right now. Pop back soon, or browse what's already in store.</p>
    <Link to="/products" className="relative">
      <Button className="h-12 px-8 rounded-full bg-health-ink hover:bg-health-emerald text-white font-bold" data-testid="empty-state-browse-button">Browse the store</Button>
    </Link>
  </motion.div>
);

const Featured = ({ products, formatLocalPrice }) => (
  <section className="py-24 bg-white" data-testid="featured-products-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <motion.div {...fadeUp()}>
          <Overline color="text-health-coral">Popular right now</Overline>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-health-ink mt-4">Customer favourites</h2>
        </motion.div>
        <Link to="/products">
          <Button variant="outline" className="h-12 px-6 rounded-full border-2 border-health-line text-health-ink hover:border-health-emerald hover:text-health-emerald font-bold" data-testid="view-all-products">
            View all <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
      {products.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {products.slice(0, 8).map((p, i) => <HealthProductCard key={p.id} product={p} formatLocalPrice={formatLocalPrice} index={i} />)}
        </div>
      ) : <EmptyShelf />}
    </div>
  </section>
);

const STEPS = [
  { icon: Search, title: 'Find what you need', desc: 'Browse by symptom or category and read clear, friendly product info.', color: 'bg-health-sky text-sky-700' },
  { icon: ShoppingBag, title: 'Check out securely', desc: 'Pay safely with your favourite method. No account needed.', color: 'bg-health-peach text-health-coral' },
  { icon: Truck, title: 'Delivered with care', desc: 'Discreetly packed and tracked all the way to your door.', color: 'bg-health-mint text-health-emerald' },
];

const HowItWorks = () => (
  <section className="py-24 bg-background">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <motion.div {...fadeUp()} className="max-w-2xl mb-14">
        <Overline>How it works</Overline>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-health-ink mt-4">Easy as one, two, three</h2>
      </motion.div>
      <div className="grid md:grid-cols-3 gap-6">
        {STEPS.map((s, i) => (
          <motion.div key={s.title} {...fadeUp(i * 0.12)} whileHover={{ y: -6 }} className="relative bg-white rounded-[2rem] p-8 shadow-soft border border-health-line overflow-hidden" data-testid={`how-step-${i + 1}`}>
            <span className="absolute -right-2 -top-6 font-heading text-[120px] font-bold text-health-mint leading-none select-none">{i + 1}</span>
            <motion.div whileHover={{ rotate: [0, -10, 10, 0] }} className={`relative w-14 h-14 rounded-2xl ${s.color} flex items-center justify-center mb-6`}>
              <s.icon className="h-7 w-7" />
            </motion.div>
            <h3 className="relative font-heading text-xl font-bold text-health-ink mb-2">{s.title}</h3>
            <p className="relative text-slate-600">{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const CarePromise = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <motion.div {...fadeUp()} className="relative">
        <div className="absolute -inset-4 bg-health-peach rounded-[2.5rem] rotate-2" />
        <img src={PHARMACIST_IMAGE} alt="Friendly care team" className="relative rounded-[2rem] w-full aspect-[4/3] object-cover shadow-lift" loading="lazy" />
        <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity }} className="absolute -bottom-6 left-6 bg-white rounded-2xl shadow-lift px-5 py-4 flex items-center gap-3">
          <Lock className="h-6 w-6 text-health-emerald" />
          <div>
            <p className="font-heading font-bold text-health-ink leading-tight">Private &amp; secure</p>
            <p className="text-xs text-slate-500">Your orders stay between us</p>
          </div>
        </motion.div>
      </motion.div>
      <motion.div {...fadeUp(0.15)}>
        <Overline color="text-health-coral">Our care promise</Overline>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-health-ink mt-4 mb-6">Health shopping that feels like a hug</h2>
        <p className="text-slate-600 text-base md:text-lg leading-relaxed mb-8">We keep things simple: genuine over-the-counter products, honest descriptions and a team that actually replies. Stock your medicine cabinet without leaving the sofa.</p>
        <ul className="space-y-4">
          {['Only genuine, sealed, over-the-counter products', 'Clear directions & safety info on every product page', 'Friendly email support whenever you need help'].map((t, i) => (
            <motion.li key={t} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 + i * 0.1 }} className="flex items-start gap-3 text-health-ink font-medium">
              <span className="w-7 h-7 rounded-full bg-health-mint text-health-emerald flex items-center justify-center shrink-0"><CheckCircle2 className="h-4 w-4" /></span>
              {t}
            </motion.li>
          ))}
        </ul>
      </motion.div>
    </div>
  </section>
);

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const { formatLocalPrice } = useCurrency();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/products?featured=true`);
        if (res.data.length > 0) return setFeaturedProducts(res.data);
        const all = await axios.get(`${API}/products`);
        setFeaturedProducts(all.data);
      } catch (error) {
        console.error('Failed to fetch featured products', error);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-background font-body text-health-ink">
      <Navbar />
      <Hero />
      <TrustMarquee />
      <Categories />
      <Featured products={featuredProducts} formatLocalPrice={formatLocalPrice} />
      <HowItWorks />
      <CarePromise />
      <Footer />
    </div>
  );
};

export default HomePage;
